import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useAccessControl } from "@/hooks/useAccessControl";
import { requestPlan } from "../concierge/api";
import { applyPlan } from "../concierge/applyPlan";
import PlanPreview from "../concierge/PlanPreview";
import { trackOnboarding } from "../analytics";
import type { ProvisioningPlan } from "../concierge/types";
import type { OnboardingAnswers } from "../types";

interface StepConciergeProps {
  answers: OnboardingAnswers;
  onComplete: (appliedTemplateId: string) => void;
  /** Fall back to the manual template path. */
  onFallback: () => void;
}

type Phase = "loading" | "preview" | "applying" | "error";

/**
 * Concierge branch of the welcome flow: the user's free text goes to the NL
 * setup service; the returned plan is previewed (editable ticker chips — the
 * hallucination backstop) and applied entirely client-side with the user's
 * own token.
 */
export default function StepConcierge({
  answers,
  onComplete,
  onFallback,
}: StepConciergeProps) {
  const { hasAccessToWidget } = useAccessControl();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>("loading");
  const [plan, setPlan] = useState<ProvisioningPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    trackOnboarding("concierge_requested");
    const query = [
      answers.freeText?.trim(),
      answers.assetClasses.length
        ? `(asset classes: ${answers.assetClasses.join(", ")})`
        : null,
    ]
      .filter(Boolean)
      .join(" ");
    requestPlan(query, "onboarding")
      .then((response) => {
        setPlan(response.plan);
        setPhase("preview");
      })
      .catch((err) => {
        console.error("[concierge] plan request failed:", err);
        setError(
          err?.response?.data?.detail ??
            "We couldn't build a plan right now — you can pick a template instead."
        );
        setPhase("error");
      });
  }, [answers]);

  const handleApply = async () => {
    if (!plan) return;
    setPhase("applying");
    try {
      const result = await applyPlan(plan, { hasAccessToWidget });
      // New resources should appear in the builders immediately.
      queryClient.invalidateQueries({ queryKey: ["get-portfolios"] });
      queryClient.invalidateQueries({ queryKey: ["tagsWatchlists"] });
      queryClient.invalidateQueries({ queryKey: ["headlineQueries"] });
      trackOnboarding("concierge_applied");
      toast.success(
        result.tabTitle
          ? `Your "${result.tabTitle}" dashboard is ready`
          : "Your setup has been created"
      );
      onComplete("concierge");
    } catch (err) {
      console.error("[concierge] apply failed:", err);
      toast.error(
        "Something went wrong applying the plan — anything already created is in Watchlists & Filters."
      );
      setPhase("preview");
    }
  };

  if (phase === "loading") {
    return (
      <div className="py-10 text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-gray-600 border-t-squawk-blue" />
        <p className="mt-4 text-sm text-gray-300">
          Building your setup from what you told us…
        </p>
        <p className="mt-1 text-xs text-gray-500">
          This usually takes 10–20 seconds.
        </p>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-gray-300">{error}</p>
        <button
          type="button"
          onClick={onFallback}
          className="mt-4 rounded-md bg-squawk-blue px-5 py-2 text-sm font-semibold text-white hover:bg-squawk-blue/90 transition-colors cursor-pointer"
        >
          Pick a template instead
        </button>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-100">
        Here&apos;s what we&apos;ll set up
      </h3>
      <p className="mt-1 text-sm text-gray-400">
        Review and trim anything you don&apos;t want — nothing is created until
        you apply.
      </p>

      {plan && (
        <div className="mt-4 max-h-80 overflow-y-auto pr-1">
          <PlanPreview plan={plan} onChange={setPlan} />
        </div>
      )}

      <div className="mt-5 flex flex-col sm:flex-row items-center gap-3">
        <button
          type="button"
          onClick={handleApply}
          disabled={phase === "applying"}
          className="w-full sm:w-auto rounded-md bg-squawk-blue px-5 py-2.5 text-sm font-semibold text-white hover:bg-squawk-blue/90 transition-colors disabled:opacity-60 cursor-pointer"
        >
          {phase === "applying" ? "Setting up…" : "Apply this setup"}
        </button>
        <button
          type="button"
          onClick={onFallback}
          disabled={phase === "applying"}
          className="text-sm text-gray-400 hover:text-gray-200 transition-colors bg-transparent border-none cursor-pointer"
        >
          Use a template instead
        </button>
      </div>
    </div>
  );
}
